import {
  Field,
  IconButton,
  SingleSelectDropdown,
  Spinner,
  Text,
  TextInput,
} from "@carbon6/c6-frontend-design";
import type { ChangeEvent, ReactNode } from "react";
import type { Dataset, DatasetDetails } from "@/api";
import { hatcheryDropdownModes, hatcheryTextModes } from "@/app/theme";
import { DATA_ORIGIN_OPTIONS } from "@/components/dataset/dataOrigin";
import { formatBytes, formatNumber } from "@/utils";
import { formatDate, formatDateTime, parseUtcMs } from "@/utils/datetime";
import type { LoadState } from "@/components/dataset/loadState";
import { ModifiedMark } from "@/components/dataset/diff";
import { storageBackendLabel } from "@/components/dataset/storageBackendText";
import { noticeChipClassName } from "@/shared/ui/emptyState/public";
import { inlineSpinnerClassName } from "@/shared/ui/loadingState/public";
import { RecordingModeCard } from "../review/RecordingModeCard";
import { configurationDiff, DIFF_AXIS } from "../review/reviewDiffModel";
import type { DatasetReviewBinding } from "../review/useDatasetReview";
import styles from "./DatasetOverviewPanel.module.css";

export type DatasetInfoCardProps = {
  dataset: Dataset | null;
  review: DatasetReviewBinding;
  details?: LoadState<DatasetDetails>;
};

/** Dataset 정보도 공통 리뷰 draft 를 편집하고, 하단의 같은 저장·확정 액션을 쓴다. */
export function DatasetInfoCard({ dataset, review, details }: DatasetInfoCardProps): ReactNode {
  const { controller, snapshot, members, membersState } = review;
  const { draft, baselineDraft } = snapshot;
  const locked =
    snapshot.loadState !== "loaded" ||
    snapshot.busy ||
    snapshot.conflict !== null ||
    !(snapshot.permissions?.can_propose || snapshot.permissions?.can_write);
  const changed = configurationDiff(baselineDraft, draft).some(
    (entry) =>
      [DIFF_AXIS.name, DIFF_AXIS.operator, DIFF_AXIS.dataOrigin, DIFF_AXIS.recordingMode].some(
        (name) => name === entry.axis,
      ) && entry.changed,
  );
  const memberOptions = members.map((member) => ({
    value: member.uuid,
    label: member.display_name,
  }));
  if (
    draft.operatorMember &&
    !memberOptions.some((option) => option.value === draft.operatorMember?.uuid)
  ) {
    memberOptions.unshift({
      value: draft.operatorMember.uuid,
      label: draft.operatorMember.display_name,
    });
  }
  return (
    <section className={styles.card} aria-label="Dataset 정보">
      <div className={styles.cardHeader}>
        <Text variant={hatcheryTextModes["caption-bold"]}>
          Dataset 정보
          <ModifiedMark changed={changed} />
        </Text>
      </div>
      <div className={styles.infoForm}>
        <Field label="이름" layout="inline" labelWidth="5rem" disabled={locked}>
          <TextInput
            aria-label="이름"
            value={draft.displayName}
            maxLength={255}
            required
            disabled={locked}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              controller.setMetadata({ displayName: event.target.value })
            }
          />
        </Field>
        {snapshot.loadState === "loaded" && !draft.displayName.trim() ? (
          <p className={styles.errorText}>이름을 입력해 주세요.</p>
        ) : null}
        <Field label="담당자" layout="inline" labelWidth="5rem" disabled={locked}>
          <div className={styles.fieldRow}>
            <SingleSelectDropdown
              mode={hatcheryDropdownModes.default}
              label="담당자"
              value={draft.operatorMember?.uuid ?? null}
              options={memberOptions}
              searchable
              searchPlaceholder="이름을 검색해 보세요."
              placeholder="선택 안 함"
              emptyMessage="선택할 멤버가 없습니다."
              disabled={locked || membersState !== "loaded"}
              onValueChange={(uuid) => {
                const member = members.find((entry) => entry.uuid === uuid);
                if (member) controller.setMetadata({ operatorMember: member });
              }}
            />
            <IconButton
              text="비우기"
              aria-label="담당자 비우기"
              type="button"
              variant="surface"
              disabled={locked || draft.operatorMember === null}
              onClick={() => controller.setMetadata({ operatorMember: null })}
            />
          </div>
        </Field>
        {membersState === "error" ? (
          <p className={styles.errorText}>
            담당자 후보를 불러오지 못했습니다. 리뷰를 다시 열어 주세요.
          </p>
        ) : null}
        {membersState === "loading" ? (
          <p className={`${styles.muted} ${styles.loadingLine}`}>
            <Spinner size="sm" className={inlineSpinnerClassName} />
            담당자 후보를 불러오고 있습니다.
          </p>
        ) : null}
        <Field label="행동 주체" layout="inline" labelWidth="5rem" disabled={locked}>
          <div className={styles.fieldRow}>
            <SingleSelectDropdown
              mode={hatcheryDropdownModes.default}
              label="행동 주체"
              value={draft.dataOrigin}
              options={DATA_ORIGIN_OPTIONS}
              placeholder="선택 안 함"
              disabled={locked}
              onValueChange={(value) => {
                const option = DATA_ORIGIN_OPTIONS.find((entry) => entry.value === value);
                if (option) controller.setMetadata({ dataOrigin: option.value });
              }}
            />
            <IconButton
              text="비우기"
              aria-label="행동 주체 비우기"
              type="button"
              variant="surface"
              disabled={locked || draft.dataOrigin === null}
              onClick={() => controller.setMetadata({ dataOrigin: null })}
            />
          </div>
        </Field>
        <RecordingModeCard
          draft={draft}
          locked={locked}
          onChange={(recordingMode) => controller.setMetadata({ recordingMode })}
        />
        {dataset ? (
          <div className={styles.facts} aria-label="저장 위치">
            <span>저장 위치 {storageBackendLabel(dataset.storage_backend)}</span>
          </div>
        ) : null}
        {details?.status === "success" ? (
          <div className={styles.facts} aria-label="파일 요약">
            <span>전체 크기 {formatBytes(details.data.total_size_bytes, "—")}</span>
            <span>추가 파일 {formatNumber(details.data.extra_file_count)}개</span>
          </div>
        ) : details?.status === "error" ? (
          <span className={`${noticeChipClassName} ${styles.failureChip}`}>
            파일 요약을 불러오지 못했습니다.
          </span>
        ) : details ? (
          <p className={`${styles.muted} ${styles.loadingLine}`}>
            <Spinner size="sm" className={inlineSpinnerClassName} />
            파일 요약을 불러오고 있습니다.
          </p>
        ) : null}
        {dataset ? (
          <div className={styles.facts} aria-label="생성 및 수정일">
            <span title={formatDateTime(parseUtcMs(dataset.created_at))}>
              생성 {formatDate(parseUtcMs(dataset.created_at))}
            </span>
            <span title={formatDateTime(parseUtcMs(dataset.modified_at))}>
              수정 {formatDate(parseUtcMs(dataset.modified_at))}
            </span>
          </div>
        ) : (
          <p className={`${styles.muted} ${styles.loadingLine}`}>
            <Spinner size="sm" className={inlineSpinnerClassName} />
            Dataset 정보를 불러오고 있습니다.
          </p>
        )}
      </div>
    </section>
  );
}
